<template>
  <div class="kendo-spreadsheet-container">
    <div id="spreadsheet" ref="spreadsheetElem" style="width: 100%;"></div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { logger } from '@/services/logger';

export default {
  name: 'KendoSpreadsheet',
  setup() {
    const spreadsheetElem = ref(null);
    let spreadsheet = null;
    const isMounted = ref(false);
    
    // Method to access the spreadsheet instance
    const getSpreadsheet = () => {
      return spreadsheet;
    };
    
    // Method to load data from a file
    const loadFromFile = (fileData) => {
      if (spreadsheet) {
        try {
          spreadsheet.fromFile(fileData);
          logger.info('File loaded into spreadsheet successfully');
          return true;
        } catch (error) {
          logger.error('Error loading file into spreadsheet', { error });
          return false;
        }
      }
      return false;
    };
    
    // Method to load data from CSV
    const loadFromCSV = (csvContent) => {
      if (spreadsheet) {
        try {
          spreadsheet.fromJSON({
            sheets: [{
              rows: csvContent.split('\n').map(line => ({
                cells: line.split(',').map(cell => ({ value: cell.trim() }))
              }))
            }]
          });
          logger.info('CSV loaded into spreadsheet successfully');
          return true;
        } catch (error) {
          logger.error('Error loading CSV into spreadsheet', { error });
          return false;
        }
      }
      return false;
    };

    onMounted(async () => {
      await loadDependencies();

      if (window.kendoLicenseInitializer) {
        window.kendoLicenseInitializer();
      }

      await initializeSpreadsheet();
      isMounted.value = true;
    });

    onBeforeUnmount(() => {
      if (spreadsheet) {
        try {
          spreadsheet.destroy();
        } catch (error) {
          logger.error('Error destroying spreadsheet', { error });
        }
      }
    });

    const loadDependencies = async () => {
      if (!window.jQuery) {
        await loadScript('https://code.jquery.com/jquery-3.7.0.min.js');
      }

      if (!window.JSZip) {
        await loadScript('https://unpkg.com/jszip/dist/jszip.min.js');
      }

      if (!window.kendo) {
        loadCSS('https://kendo.cdn.telerik.com/themes/10.2.0/default/default-main.css');

        await loadScript('https://kendo.cdn.telerik.com/2025.1.227/js/kendo.all.min.js');
      }
    };

    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const loadCSS = (href) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    };

    const initializeSpreadsheet = async () => {
      const $ = window.jQuery;
      
      if (!spreadsheetElem.value) {
        logger.error('Spreadsheet element not found');
        return;
      }
      
      // Add event listener to maintain modal size when tabs are switched
      $(document).on('click', '.k-tabstrip-item', function() {
        setTimeout(() => {
          $('.k-window, .k-dialog, .k-spreadsheet-window').css({
            'min-width': '1000px',
            'width': 'auto'
          });
          $('.k-window-content, .k-popup-content, .k-content, .k-tabstrip-content').css({
            'min-width': '980px',
            'width': 'auto'
          });
        }, 10); // Small delay to ensure the tab switch has completed
      });

      if (spreadsheet) {
        try {
          spreadsheet.destroy();
        } catch (error) {
          logger.error('Error destroying existing spreadsheet', { error });
        }
      }

      // Register custom color editor
      if (window.kendo && window.kendo.spreadsheet) {
        window.kendo.spreadsheet.registerEditor("color", function(){
          var context, dlg, model; // Removed unused 'colorpicker' variable
          
          // Define create function at the top level of its scope
          const create = function() {
            if (!dlg) {
              model = window.kendo.observable({
                value: "#000000",
                ok: function() {
                  context.callback(model.value);
                  dlg.close();
                },
                cancel: function() {
                  dlg.close();
                }
              });
              var el = $("<div data-visible='true' data-role='window' data-modal='true' data-resizable='false' data-title='Select color'>" +
                        "  <div data-role='flatcolorpicker' data-bind='value: value'></div>" +
                        "  <div style='margin-top: 1em; text-align: right'>" +
                        "    <button style='width: 6em' class='k-button k-button-solid-base k-button-solid k-button-md k-rounded-md' data-bind='click: ok'>OK</button>" +
                        "    <button style='width: 6em' class='k-button k-button-solid-primary k-button-solid k-button-md k-rounded-md' data-bind='click: cancel'>Cancel</button>" +
                        "  </div>" +
                        "</div>");
              window.kendo.bind(el, model);
              dlg = el.getKendoWindow();
            }
          };
          
          // Define open function at the top level of its scope
          const open = function() {
            create();
            dlg.open();
            dlg.center();
            var value = context.range.value();
            if (value != null) {
              model.set("value", value);
            }
          };
          
          return {
            edit: function(options) {
              context = options;
              open();
            },
            icon: "k-i-background"
          }
        });
      }

      // Initialize empty spreadsheet
      $(spreadsheetElem.value).kendoSpreadsheet({
        columns: 20,
        rows: 20,
        sheetsbar: true,
        excel: {
          proxyURL: "https://demos.telerik.com/kendo-ui/service/export"
        },
        toolbar: {
          home: [
            "open", "save", "cut", "copy", "paste",
            "bold", "italic", "underline",
            "fontSize", "fontFamily", "alignment",
            "textColor", "backgroundColor", "borders",
            "format"
          ]
        }
      });

      spreadsheet = $(spreadsheetElem.value).data("kendoSpreadsheet");
      
      return spreadsheet;
    };

    // This function is kept for potential future use to load data from API
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const loadSampleData = async () => {
      try {
        const response = await fetch('/api/spreadsheet/data');
        const data = await response.json();
        spreadsheet.fromJSON({ sheets: data });
      } catch (error) {
        logger.error('Error loading spreadsheet data', { error });
        // Default data is now handled in the initializeSpreadsheet method
      }
    };

    return {
      spreadsheetElem,
      isMounted,
      getSpreadsheet,
      loadFromFile,
      loadFromCSV
    };
  }
}
</script>

<style scoped>
/* Fix for dropdown list width */
:deep(.k-list) {
  width: max-content;
}

/* Additional fix for tab content */
:deep(.k-tabstrip-content) {
  box-sizing: border-box !important;
  padding: 10px !important;
}

.kendo-spreadsheet-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

#spreadsheet {
  flex: 1;
  min-height: 500px;
}
</style>
